<script setup lang="ts">
import '#layers/ams/app/assets/css/ams.css'

type Step = 'request' | 'confirm'

const toast = useToast()

const step = ref<Step>('request')
const loading = ref(false)
const error = ref<string | null>(null)

const username = ref('')
const challenge = ref('')
const code = ref('')
const password = ref('')
const passwordRepeat = ref('')

// Gleiche Regeln wie im Passwort-Ändern-Dialog; der Server prüft sie erneut.
const pwRules = [
  { label: 'Mindestens 8 Zeichen', test: (v: string) => v.length >= 8 },
  { label: 'Ein Großbuchstabe', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'Ein Kleinbuchstabe', test: (v: string) => /[a-z]/.test(v) },
  { label: 'Eine Zahl', test: (v: string) => /[0-9]/.test(v) },
]

const passwordValid = computed(() => pwRules.every((rule) => rule.test(password.value)))
const passwordsMatch = computed(
  () => password.value.length > 0 && password.value === passwordRepeat.value
)
const codeValid = computed(() => /^\d{8}$/.test(code.value.replace(/\s+/g, '')))

function getErrorMessage(err: unknown, fallback: string) {
  const e = err as { data?: { statusMessage?: string }; statusMessage?: string }
  return e?.data?.statusMessage || e?.statusMessage || fallback
}

async function requestCode() {
  error.value = null
  loading.value = true
  try {
    const response = await $fetch<{ challenge: string }>(
      '/api/auth/password-reset/request',
      { method: 'POST', body: { username: username.value } }
    )
    challenge.value = response.challenge
    code.value = ''
    step.value = 'confirm'
  } catch (err) {
    error.value = getErrorMessage(err, 'Anfrage fehlgeschlagen. Bitte versuche es erneut.')
  } finally {
    loading.value = false
  }
}

async function confirmReset() {
  error.value = null
  if (!codeValid.value) {
    error.value = 'Der Code besteht aus 8 Ziffern.'
    return
  }
  if (!passwordValid.value) {
    error.value = 'Das Passwort erfüllt die Regeln nicht.'
    return
  }
  if (!passwordsMatch.value) {
    error.value = 'Die Passwörter stimmen nicht überein.'
    return
  }

  loading.value = true
  try {
    await $fetch('/api/auth/password-reset/confirm', {
      method: 'POST',
      body: {
        challenge: challenge.value,
        code: code.value,
        password: password.value,
      },
    })
    toast.add({
      title: 'Passwort geändert',
      description: 'Du kannst dich jetzt mit dem neuen Passwort anmelden.',
      color: 'success',
      icon: 'i-lucide-circle-check',
    })
    await navigateTo('/auth/login')
  } catch (err) {
    error.value = getErrorMessage(err, 'Passwort konnte nicht geändert werden.')
  } finally {
    loading.value = false
  }
}

function backToRequest() {
  step.value = 'request'
  error.value = null
  challenge.value = ''
  code.value = ''
  password.value = ''
  passwordRepeat.value = ''
}

definePageMeta({
  layout: false,
  guest: true,
})
</script>

<template>
  <div class="sci-fi-grid min-h-screen">
    <div class="flex h-auto min-h-[calc(100vh-6rem)] lg:h-screen w-full">
      <div
        class="w-lg max-w-full m-auto bg-(--ui-bg-muted) relative rounded-xl p-6 pb-8 prose prose-invert border-(--ui-bg-accented)"
      >
        <video
          @contextmenu.prevent
          autoplay
          muted
          loop
          playsinline
          webkit-playsinline
          :src="
            $config.public.ASSETS_URL + 'fcb6b51a-c3b4-44b0-888d-2462f4197e55'
          "
          class="mx-auto w-72 h-12 object-cover"
        />

        <form
          v-if="step === 'request'"
          class="mt-0 flex flex-col gap-y-8"
          @submit.prevent="requestCode"
        >
          <h2 class="text-center mt-2">Passwort zurücksetzen</h2>
          <p class="text-sm text-center text-muted m-0">
            Gib deinen Benutzernamen ein. Wir senden dir einen Code per
            Discord-Nachricht an den in deinem Profil hinterlegten Account.
          </p>
          <UFormField required name="username" label="Benutzername">
            <UInput
              v-model="username"
              required
              size="xl"
              type="text"
              placeholder="chris.roberts"
              autocomplete="username"
              class="w-full"
            />
          </UFormField>
          <UButton
            :loading="loading"
            variant="subtle"
            size="xl"
            type="submit"
            label="Code anfordern"
            class="w-full flex justify-center"
          />
          <p v-if="error" class="text-sm text-error m-0">{{ error }}</p>
          <NuxtLink to="/auth/login" class="text-sm text-center">
            Zurück zum Login
          </NuxtLink>
        </form>

        <form
          v-else
          class="mt-0 flex flex-col gap-y-6"
          @submit.prevent="confirmReset"
        >
          <h2 class="text-center mt-2">Neues Passwort festlegen</h2>
          <p class="text-sm text-center text-muted m-0">
            Falls für diesen Benutzer ein Discord-Account hinterlegt ist, wurde
            ein Code gesendet (10 Minuten gültig). Ohne hinterlegten
            Discord-Account ist ein Zurücksetzen hier nicht möglich — wende
            dich in diesem Fall an einen Admin.
          </p>
          <UFormField required name="code" label="Code aus der Discord-Nachricht">
            <UInput
              v-model="code"
              required
              size="xl"
              type="text"
              inputmode="numeric"
              placeholder="1234 5678"
              autocomplete="one-time-code"
              maxlength="9"
              class="w-full"
            />
          </UFormField>
          <UFormField required name="password" label="Neues Passwort">
            <UInput
              v-model="password"
              required
              size="xl"
              type="password"
              placeholder="*********"
              autocomplete="new-password"
              class="w-full"
            />
          </UFormField>
          <ul class="list-none p-0 m-0 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <li
              v-for="rule in pwRules"
              :key="rule.label"
              class="m-0 p-0 flex items-center gap-1.5"
              :class="rule.test(password) ? 'text-success' : 'text-muted'"
            >
              <UIcon
                :name="rule.test(password) ? 'i-lucide-check' : 'i-lucide-circle'"
                class="size-3.5"
              />
              {{ rule.label }}
            </li>
          </ul>
          <UFormField required name="passwordRepeat" label="Passwort wiederholen">
            <UInput
              v-model="passwordRepeat"
              required
              size="xl"
              type="password"
              placeholder="*********"
              autocomplete="new-password"
              class="w-full"
            />
          </UFormField>
          <UButton
            :loading="loading"
            :disabled="!codeValid || !passwordValid || !passwordsMatch"
            variant="subtle"
            size="xl"
            type="submit"
            label="Passwort ändern"
            class="w-full flex justify-center"
          />
          <p v-if="error" class="text-sm text-error m-0">{{ error }}</p>
          <div class="flex justify-between text-sm">
            <button
              type="button"
              class="underline cursor-pointer"
              @click="backToRequest"
            >
              Neuen Code anfordern
            </button>
            <NuxtLink to="/auth/login">Zurück zum Login</NuxtLink>
          </div>
        </form>
      </div>
    </div>
    <AMSUiElementsMicrotechOs />
  </div>
</template>
